import { createFileRoute, Outlet, useRouterState } from '@tanstack/react-router'
import { useAuthStore } from '@/stores/auth-store'
import { requireAdmin } from '@/lib/auth-guards'
import { cn } from '@/lib/utils'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { ConfigDrawer } from '@/components/config-drawer'
import { Header } from '@/components/layout/header'
import { Main } from '@/components/layout/main'
import { ProfileDropdown } from '@/components/profile-dropdown'
import { Search } from '@/components/search'
import { ThemeSwitch } from '@/components/theme-switch'
import { MembershipsDialogs } from '@/features/memberships/components/memberships-dialogs'
import { MembershipsProvider } from '@/features/memberships/components/memberships-provider'

const membershipTabs = [
  { value: 'list', label: 'Planes', to: '/memberships' },
  {
    value: 'without-membership',
    label: 'Sin Plan activo',
    to: '/memberships/without-membership',
  },
  {
    value: 'manage-types',
    label: 'Administrar Planes',
    to: '/memberships/manage-types',
  },
  {
    value: 'day-pass-types',
    label: 'Pases diarios',
    to: '/memberships/day-pass-types',
  },
]

const superAdminMembershipTabs = [
  {
    value: 'compensations',
    label: 'Compensar días',
    to: '/memberships/compensations',
  },
]

export const Route = createFileRoute('/_authenticated/memberships')({
  beforeLoad: requireAdmin,
  component: MembershipsLayout,
})

function MembershipsLayout() {
  const isSuperAdmin = useAuthStore((state) => state.isSuperAdmin())
  const tabs = isSuperAdmin
    ? [...membershipTabs, ...superAdminMembershipTabs]
    : membershipTabs
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  })
  const activeTab =
    tabs.find((tab) => pathname === tab.to)?.value ??
    tabs.find((tab) => pathname.startsWith(`${tab.to}/`))?.value ??
    tabs[0].value

  return (
    <MembershipsProvider>
      <Header fixed>
        <Search />
        <div className='ms-auto flex items-center space-x-4'>
          <ThemeSwitch />
          <ConfigDrawer />
          <ProfileDropdown />
        </div>
      </Header>

      <Main className='flex flex-1 flex-col gap-4 sm:gap-6'>
        <div>
          <h2 className='text-2xl font-bold tracking-tight'>Planes</h2>
          <p className='text-muted-foreground'>
            Gestiona planes, renovaciones e historial
          </p>
        </div>

        <Tabs value={activeTab} className='w-full space-y-4'>
          {/* Scrolls sideways when the tabs don't fit; a full-width grid on desktop */}
          <div className='w-full overflow-x-auto pb-2 lg:overflow-visible lg:pb-0'>
            <TabsList
              className={cn(
                'lg:grid lg:w-full',
                isSuperAdmin ? 'lg:grid-cols-5' : 'lg:grid-cols-4'
              )}
            >
              {tabs.map((tab) => (
                <TabsTrigger key={tab.value} value={tab.value} asChild>
                  <Route.Link to={tab.to}>{tab.label}</Route.Link>
                </TabsTrigger>
              ))}
            </TabsList>
          </div>
          <TabsContent value={activeTab} className='space-y-4'>
            <Outlet />
          </TabsContent>
        </Tabs>
      </Main>

      <MembershipsDialogs />
    </MembershipsProvider>
  )
}
